/**
 * IssueReportsMine.jsx — reporter-facing "My Reports" pane.
 *
 * Iter 338 (Feb 2026): non-admin users open Settings → Issue Reports
 * and see the reports they submitted, along with any comments the
 * Support team has posted (fix notes, resolution details, or requests
 * for more info). Comments are one-way from Support for now.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/App";
import { useIssueDeepLink, IssueDeepLinkStyles } from "@/components/useIssueDeepLink";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_TINT = {
  new:         "#2563eb",
  triaging:    "#a16207",
  in_progress: "#7c3aed",
  fixed:       "#16a34a",
  wontfix:     "#6b7280",
};
const STATUS_LABEL = {
  new: "New", triaging: "Triaging", in_progress: "In progress",
  fixed: "Fixed", wontfix: "Won't fix",
};
const KIND_TINT = {
  bug:     { fg: "#dc2626", bg: "rgba(220,38,38,0.10)", label: "Bug" },
  feature: { fg: "#7c3aed", bg: "rgba(124,58,237,0.10)", label: "Improvement" },
  comment: { fg: "#2563eb", bg: "rgba(37,99,235,0.10)", label: "Comment" },
  other:   { fg: "#a16207", bg: "rgba(161,98,7,0.10)", label: "Other" },
};

function StatusBadge({ status }) {
  const tint = STATUS_TINT[status] || "#6b7280";
  const label = STATUS_LABEL[status] || status;
  return (
    <span
      data-testid={`mine-status-${status}`}
      style={{
        padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
        border: `0.5px solid ${tint}`, color: tint, background: "transparent",
      }}
    >
      {label}
    </span>
  );
}

function KindChip({ kind }) {
  const k = KIND_TINT[kind] || KIND_TINT.bug;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
      color: k.fg, background: k.bg,
      borderRadius: 4, padding: "2px 6px",
    }}>{k.label}</span>
  );
}

// ── Iter 188.7 (Feb 2026) — Stage + TAT chips ─────────────────────
// Reporter view now surfaces the coarse-grained lifecycle Stage
// (Open / In progress / Closed) alongside the fine-grained Status
// chip, and a live TAT/Remaining Time chip driven by the
// `hours_remaining` / `tat_breached` / `tat_frozen` fields the
// backend computes from `severity + created_at + resolved_at`. No
// existing badge is removed — new chips slot in as siblings so the
// deep-link, expand, and comment flows are untouched.

const STAGE_TINT = {
  "Open":         { fg: "#2563eb", bg: "rgba(37,99,235,0.10)" },
  "In progress":  { fg: "#7c3aed", bg: "rgba(124,58,237,0.10)" },
  "Closed":       { fg: "#6b7280", bg: "rgba(107,114,128,0.10)" },
};

function StageChip({ stage }) {
  const t = STAGE_TINT[stage] || STAGE_TINT.Open;
  return (
    <span
      data-testid={`mine-stage-${(stage || "open").toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
        color: t.fg, background: t.bg,
        borderRadius: 4, padding: "2px 6px",
      }}
    >
      {stage || "Open"}
    </span>
  );
}

// Turn `hours_remaining` (float) into a compact "2d 4h" / "3h 12m" /
// "6m" string. Negative values are formatted as "1d 6h overdue".
function _fmtDurationHours(hoursRaw) {
  if (hoursRaw == null || !Number.isFinite(hoursRaw)) return "—";
  const overdue = hoursRaw < 0;
  const total = Math.abs(hoursRaw);
  const days = Math.floor(total / 24);
  const hours = Math.floor(total - days * 24);
  const minutes = Math.max(0, Math.round((total - Math.floor(total)) * 60));
  let compact;
  if (days > 0) compact = `${days}d ${hours}h`;
  else if (hours > 0) compact = `${hours}h ${minutes}m`;
  else compact = `${minutes}m`;
  return overdue ? `${compact} overdue` : `${compact} left`;
}

function TatChip({ report }) {
  const hr = report?.hours_remaining;
  const breached = !!report?.tat_breached;
  const frozen = !!report?.tat_frozen;
  const slaHours = report?.tat_hours;
  if (hr == null || slaHours == null) return null;

  // Colour scheme:
  //   frozen + within SLA  → muted green (closed under SLA)
  //   frozen + breached    → muted red   (closed past SLA)
  //   breached (live)      → red
  //   < 20% of SLA left    → amber
  //   > 20% of SLA left    → grey
  let fg, bg;
  if (frozen && !breached) {
    fg = "#15803d"; bg = "rgba(21,128,61,0.08)";
  } else if (breached) {
    fg = "#dc2626"; bg = "rgba(220,38,38,0.10)";
  } else if (hr / slaHours < 0.2) {
    fg = "#b45309"; bg = "rgba(180,83,9,0.10)";
  } else {
    fg = "#374151"; bg = "rgba(55,65,81,0.06)";
  }

  const label = frozen
    ? (breached ? `Closed · ${_fmtDurationHours(-Math.abs(hr))}` : `Closed · ${_fmtDurationHours(hr)}`)
    : _fmtDurationHours(hr);

  const tip = (
    `SLA: ${slaHours}h from submission` +
    (report?.tat_deadline_utc ? ` · Due ${new Date(report.tat_deadline_utc).toLocaleString()}` : "")
  );

  return (
    <span
      data-testid={`mine-tat-${report.ticket_id || report.id}`}
      title={tip}
      style={{
        fontSize: 11, fontWeight: 600,
        color: fg, background: bg,
        border: `0.5px solid ${fg}33`,
        padding: "2px 8px", borderRadius: 999,
      }}
    >
      {label}
    </span>
  );
}

export default function IssueReportsMine() {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const { ticketId: deepLinkTicket, registerRow } = useIssueDeepLink();

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/issue-reports/mine?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(r.data?.items || []);
    } catch {
      toast.error("Couldn't load your reports");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  // Iter 338.1 — reporter can now reply back to Support while the ticket
  // is still open. Backend enforces the same policy (open statuses only)
  // and 409s on closed tickets, so this is defence-in-depth.
  const postReply = useCallback(async (reportId, text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return false;
    try {
      await axios.post(
        `${API}/api/issue-reports/${reportId}/comments`,
        { text: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Reply sent to Support");
      fetchMine();
      return true;
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "ticket_closed") {
        toast.error("This ticket is closed — reopen it via Support to keep the conversation going.");
      } else {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't post reply");
      }
      return false;
    }
  }, [token, fetchMine]);

  // Deep-link support: if URL has ?ticket=BUG-0123, auto-expand that row.
  // Scroll + pulse highlight is handled by useIssueDeepLink via the row ref.
  useEffect(() => {
    if (!reports.length || !deepLinkTicket) return;
    const hit = reports.find((r) => r.ticket_id === deepLinkTicket);
    if (hit) setExpanded((prev) => (prev[hit.id] ? prev : { ...prev, [hit.id]: true }));
  }, [reports, deepLinkTicket]);

  const totalComments = useMemo(
    () => reports.reduce((n, r) => n + (r.support_comments?.length || 0), 0),
    [reports]
  );

  return (
    <div data-testid="settings-issue-reports-mine">
      <IssueDeepLinkStyles />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>My reports</h2>
          <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "4px 0 0 0" }}>
            Bug reports, suggestions, and comments you&apos;ve submitted. Support responses appear inline once posted.
            {totalComments > 0 && (
              <span style={{ marginLeft: 6, color: "var(--text-secondary)", fontWeight: 500 }}>
                · {totalComments} support comment{totalComments === 1 ? "" : "s"} across your reports
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="mine-refresh-btn"
          onClick={fetchMine}
          style={{
            padding: "6px 12px", border: "0.5px solid var(--border)", borderRadius: 8,
            background: "white", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          {loading ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} />
                   : <RefreshCw style={{ width: 12, height: 12 }} />}
          Refresh
        </button>
      </div>

      {reports.length === 0 ? (
        <div
          data-testid="mine-empty"
          style={{
            border: "0.5px dashed var(--border)", borderRadius: 10, padding: "32px 16px",
            textAlign: "center", color: "var(--text-tertiary)", fontSize: 13,
          }}
        >
          {loading ? "Loading…" : "You haven't submitted any reports yet. Use the floating \"Report issue\" button to file one."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {reports.map((r) => {
            const isOpen = !!expanded[r.id];
            const comments = r.support_comments || [];
            return (
              <div
                key={r.id}
                data-testid={`mine-report-row-${r.ticket_id || r.id}`}
                ref={(node) => r.ticket_id && registerRow(r.ticket_id, node)}
                style={{ border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}
              >
                <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <button
                    type="button"
                    data-testid={`mine-expand-${r.ticket_id || r.id}`}
                    onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                    style={{ background: "none", border: 0, cursor: "pointer", padding: 0, color: "var(--text-tertiary)", marginTop: 2 }}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                  >
                    {isOpen ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      {r.ticket_id && (
                        <span
                          data-testid={`mine-ticket-id-${r.ticket_id}`}
                          style={{
                            fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 700,
                            color: "var(--text-secondary)", background: "var(--bg-subtle, #f3f4f6)",
                            padding: "2px 6px", borderRadius: 4,
                          }}
                        >
                          {r.ticket_id}
                        </span>
                      )}
                      <KindChip kind={r.kind || "bug"} />
                      <StageChip stage={r.stage} />
                      <StatusBadge status={r.status} />
                      <TatChip report={r} />
                      <span
                        data-testid={`mine-escalated-date-${r.ticket_id || r.id}`}
                        style={{ fontSize: 11, color: "var(--text-tertiary)" }}
                        title="Escalated / submitted date"
                      >
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                      {comments.length > 0 && (
                        <span
                          data-testid={`mine-comment-count-${r.ticket_id || r.id}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            fontSize: 11, fontWeight: 600, color: "#2563eb",
                            padding: "2px 8px", borderRadius: 999,
                            background: "rgba(37,99,235,0.10)",
                          }}
                        >
                          <MessageSquare style={{ width: 11, height: 11 }} />
                          {comments.length} from Support
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {r.description}
                    </div>
                    {r.route && (
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 6, fontFamily: "ui-monospace, monospace" }}>
                        {r.route}
                      </div>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div
                    data-testid={`mine-expanded-${r.ticket_id || r.id}`}
                    style={{
                      borderTop: "0.5px solid var(--border)",
                      background: "var(--bg-subtle, #f9fafb)",
                      padding: "14px 18px 16px 36px",
                    }}
                  >
                    <CommentsThread comments={comments} readOnly />
                    <MineReplyBox
                      report={r}
                      onSubmit={(text) => postReply(r.id, text)}
                    />
                    {r.resolution_message && (
                      <div
                        data-testid={`mine-resolution-${r.ticket_id || r.id}`}
                        style={{
                          marginTop: 12, padding: "10px 12px",
                          background: "white", border: "0.5px solid rgba(22,163,74,0.30)",
                          borderRadius: 8,
                        }}
                      >
                        <div style={{
                          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                          letterSpacing: "0.06em", color: "#16a34a", marginBottom: 4,
                        }}>
                          Resolution note
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
                          {r.resolution_message}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Inline reply box shown on each expanded report row in the reporter's
 * "My Reports" view. Hidden once the ticket is closed (fixed / wontfix) so
 * the UI mirrors the backend's close-out semantics.
 */
function MineReplyBox({ report, onSubmit }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const closed = report.status === "fixed" || report.status === "wontfix";

  const handleSubmit = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    const ok = await onSubmit(text);
    setPosting(false);
    if (ok) setText("");
  };

  if (closed) {
    return (
      <div
        data-testid={`mine-reply-closed-${report.ticket_id || report.id}`}
        style={{
          marginTop: 12, padding: "8px 12px", fontSize: 11,
          color: "var(--text-tertiary)", background: "white",
          border: "0.5px dashed var(--border)", borderRadius: 8,
        }}
      >
        This ticket is {report.status === "fixed" ? "resolved" : "closed"} — replies are disabled.
        Need to reopen it? File a new report and reference {report.ticket_id || "this ticket"}.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <textarea
        data-testid={`mine-reply-input-${report.ticket_id || report.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="Reply to Support (add context, answer their question, or share a screenshot link)…"
        style={{
          width: "100%", padding: "8px 10px", border: "0.5px solid var(--border)",
          borderRadius: 6, fontSize: 12, fontFamily: "inherit", resize: "vertical",
          background: "white",
        }}
      />
      <div style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
          Support will get a bell notification the moment you post.
        </span>
        <button
          type="button"
          data-testid={`mine-reply-post-${report.ticket_id || report.id}`}
          onClick={handleSubmit}
          disabled={posting || !text.trim()}
          style={{
            padding: "6px 12px", border: 0,
            background: text.trim() && !posting ? "var(--navy, #0f172a)" : "#9ca3af",
            color: "white", borderRadius: 6, fontSize: 11, fontWeight: 600,
            cursor: posting || !text.trim() ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          {posting
            ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} />
            : <Send style={{ width: 12, height: 12 }} />}
          {posting ? "Posting…" : "Reply to Support"}
        </button>
      </div>
    </div>
  );
}

/**
 * Shared read-only comments list (also imported by IssueReportsAdmin
 * so the reporter's view and the admin's view render identically).
 */
export function CommentsThread({ comments, readOnly = false }) {
  if (!comments || comments.length === 0) {
    return (
      <div
        data-testid="comments-empty"
        style={{
          fontSize: 12, color: "var(--text-tertiary)", padding: "8px 0",
        }}
      >
        {readOnly
          ? "No comments yet — Support will post here when there's an update, and you can reply back below."
          : "No comments yet — add one below to update the reporter."}
      </div>
    );
  }
  return (
    <div
      data-testid="comments-thread"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--text-tertiary)", marginBottom: 2,
      }}>
        Support comments ({comments.length})
      </div>
      {comments.map((c) => {
        // Iter 338.1 — badge author group. Falls back to role-based
        // classification for older comments that pre-date author_group.
        const authorGroup = c.author_group
          || (["super_admin", "crm_admin"].includes((c.by_role || "").toLowerCase()) ? "support" : "reporter");
        const isSupport = authorGroup === "support";
        return (
          <div
            key={c.id}
            data-testid={`comment-${c.id}`}
            data-author-group={authorGroup}
            style={{
              padding: "10px 12px", background: "white",
              border: `0.5px solid ${isSupport ? "rgba(37,99,235,0.35)" : "rgba(22,163,74,0.35)"}`,
              borderLeft: `3px solid ${isSupport ? "#2563eb" : "#16a34a"}`,
              borderRadius: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
                {c.by_name || c.by_email || (isSupport ? "Support" : "Reporter")}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                padding: "1px 6px", borderRadius: 4,
                color: isSupport ? "#2563eb" : "#16a34a",
                background: isSupport ? "rgba(37,99,235,0.10)" : "rgba(22,163,74,0.10)",
              }}>
                {isSupport ? "Support" : "Reporter"}
              </span>
              {c.status_snapshot && (
                <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                  · while status was <em>{STATUS_LABEL[c.status_snapshot] || c.status_snapshot}</em>
                </span>
              )}
              <span style={{ fontSize: 10, color: "var(--text-tertiary)", marginLeft: "auto" }}>
                {new Date(c.created_at).toLocaleString()}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "var(--text-primary)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {c.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}
